import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import logoSite from '../../../assets/brand/logo_site.svg';
import logoOnLight from '../../../assets/brand/logo_site_onlight.svg';
import { CAPTION_FONTS, EXPORT_TEXT } from './exportText';
import { buildTranscriptText } from './transcriptText';
import { cropWindow, imageStyle, panView, shapeGeometry, DEFAULT_VIEW } from './framing';
import { backdropPad, spotPosition } from './logoPlace';
import { FX, shownRange, useClipClock, wordAt, wordMotion, wordTimes } from './captionClock';
import { BOX, KW, STACK, boxRect, boxState, easeInOut, easeOut, glowState, kwEnter, kwKeys, mixColour, nounSet, phraseBlocks, presetByKey, presetEngine, stackPlan } from './captionPresets';

// What the export will look like, drawn from the first ticked clip's poster + words.
// Nothing is rendered on the server. It follows the worker's recipes (tasks.py
// video_concatenate): Blur = the whole frame over a zoomed, blurred copy; Zoom = the frame
// fills 9:16 (the export then aims at the face, so an off-center speaker comes out better
// than this middle crop); captions sit 22% up on vertical, near the bottom on wide.

const CherryMark = ({ size }) => (
  <img src={logoSite} alt="" aria-hidden style={{ height: size, width: 'auto' }} />
);

/** The words on screen for one moment of the clip, the second one lit. (The picks strip
 * draws its tiny captions with this too, so they match the big preview.) */
export const captionWords = (text, mode, wordsPerGroup) => {
  const words = (text || '').split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lit = Math.min(1, words.length - 1);
  let shown;
  if (mode === 'word') shown = [lit];
  else if (mode === 'group') shown = words.slice(0, Math.max(1, wordsPerGroup)).map((_, i) => i);
  else shown = words.map((_, i) => i);
  return shown.map((i) => ({ text: words[i], lit: i === lit }));
};

/** How the letters stand off the picture — the worker's looks (tasks.py _make_ass_subtitle):
 *  outline (≈font/8), thick (≈font/4.5), box (solid black behind the line), shadow (a hard
 *  offset copy, no outline). Shared with the picks strip. */
export const lookStyle = (look) => {
  if (look === 'box') return { WebkitTextStroke: 0, textShadow: 'none' };
  if (look === 'shadow') return { WebkitTextStroke: 0, textShadow: '0.083em 0.083em 0 rgba(0,0,0,0.65)' };
  if (look === 'thick') return { WebkitTextStroke: '0.22em #000', paintOrder: 'stroke fill', textShadow: '0 0.06em 0.06em rgba(0,0,0,0.6)' };
  return { WebkitTextStroke: '0.12em #000', paintOrder: 'stroke fill', textShadow: '0 0.05em 0.05em rgba(0,0,0,0.6)' };
};
export const BOX_STYLE = { background: '#000', padding: '0.3em 0.3em', boxDecorationBreak: 'clone', WebkitBoxDecorationBreak: 'clone' };

const luma = (hex) => {
  const h = String(hex || '#ffffff').replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** One word's look under a highlight (the still frame) — the worker's caption_fx.fx_events:
 *  box = a rounded box in the highlight colour behind the spoken word (near-black letters with
 *  no outline on a very light box), pop = bigger, fill = karaoke part-way through it (the words
 *  before it already filled). Shared with the picks strip. */
export const wordStyle = (highlight, lit, palette, done = false) => {
  if (highlight === 'box' && lit) {
    const light = luma(palette.active) > 0.78;
    return {
      color: light ? '#111111' : '#FFFFFF',
      background: palette.active,
      borderRadius: '0.2em',
      padding: '0 0.1em',
      margin: '0 -0.1em',
      boxDecorationBreak: 'clone',
      WebkitBoxDecorationBreak: 'clone',
      ...(light ? { WebkitTextStroke: 0, textShadow: 'none' } : {}),
    };
  }
  if (highlight === 'pop' && lit) return { color: palette.active, display: 'inline-block', transform: 'scale(1.16)' };
  if (highlight === 'fill' && lit) {
    return {
      backgroundImage: `linear-gradient(90deg, ${palette.active} 55%, ${palette.base} 55%)`,
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    };
  }
  if (highlight === 'fill' && done) return { color: palette.active };
  if (highlight === 'glow' && lit) {
    return { color: '#FFFFFF', WebkitTextStroke: `0.06em ${palette.active}`, textShadow: `0 0 0.12em ${palette.active}, 0 0 0.3em ${palette.active}` };
  }
  if (highlight === 'underline' && lit) {
    return { color: palette.active, textDecoration: 'underline', textDecorationColor: palette.active, textDecorationThickness: '0.12em', textUnderlineOffset: '0.14em' };
  }
  if (highlight === 'wiggle' && lit) return { color: palette.active, display: 'inline-block', transform: 'rotate(-7deg)' };
  return { color: lit ? palette.active : palette.base };
};

/** A word not said yet, when words come on one by one: it keeps its place (the worker lays
 *  the whole group out first) but isn't drawn. */
export const hiddenStyle = { visibility: 'hidden' };
export const ONE_BY_ONE = ['typewriter', 'slide', 'fade'];
export const BOUNCERS = ['ball', 'cherry', 'cherryball'];

// The bouncing ball / cherry — the worker's caption_fx motion (hop_path / squash / ball_events),
// looped over the words on screen: land on a word, squash, rest, then hop to the next.
export const BOUNCE = { period: 0.6, hop: 0.42, squash: 0.11, ball: 0.34, cherry: 1.15, cherryball: 0.62 };
export const hopAt = (a, b, u, h) => [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u - 4 * h * u * (1 - u)];
export const squashAt = (u) => {
  const e = Math.abs(2 * u - 1);
  return [1 - 0.15 * e, 1 + 0.22 * e];
};

/** The soft shadow a Story Stack word has with the Shadow look (the worker's \\yshad + \\blur). */
export const STACK_SHADOW = '0 0.04em 0.14em rgba(0,0,0,0.55)';

/**
 * Story Stack: a left-aligned block of words that builds down the frame as they are said — small
 * words on small rows, the block's key word big (captionPresets.stackPlan, the worker's
 * caption_fx.stack_events). The rows are real CSS line boxes of STACK.line ems; the worker puts
 * its words where these land. A word that alone is wider than the box shrinks its row to fit.
 */
const StackCaption = ({
  all, nouns, times, t, palette, size, vertical, font, position, look, upper,
  onPointerDown, movable, onDoubleClick, testId,
}) => {
  const plan = useMemo(() => stackPlan(all, nouns), [all, nouns]);
  const rowRefs = useRef([]);
  const [fit, setFit] = useState({});
  const { k } = wordAt(times, t, 0);
  let b = plan.findIndex((rows) => rows.some(([, idx]) => idx.includes(k)));
  if (b < 0) b = 0;
  const rows = plan[b] || [];
  const scale = (font && font.scale) || 1;
  const cqw = (vertical ? size * 0.139 : size * 0.078) * scale;
  // A row whose widest word overflows the box shrinks until it fits (the worker does the same).
  useLayoutEffect(() => {
    const next = {};
    let changed = false;
    rows.forEach((row, r) => {
      const el = rowRefs.current[r];
      if (!el) return;
      const f = fit[`${b}:${r}`] || 1;
      const widest = Math.max(0, ...Array.from(el.children).map((c) => c.offsetWidth)) / f;
      const want = widest > 0 && el.clientWidth > 0 ? Math.min(1, el.clientWidth / widest) : 1;
      next[`${b}:${r}`] = want;
      if (Math.abs(want - f) > 0.002) changed = true;
    });
    if (changed) setFit(next);
  });
  const spot = position || { x: STACK.spot[vertical ? 'portrait' : 'landscape'][0], y: STACK.spot[vertical ? 'portrait' : 'landscape'][1] };
  const softShadow = look === 'shadow';
  return (
    <Box
      data-testid={testId}
      data-preset="stack"
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick || undefined}
      title={onDoubleClick ? EXPORT_TEXT.EDIT_WORDS : undefined}
      sx={{
        position: 'absolute',
        left: `${(spot.x - STACK.box / 2) * 100}%`,
        top: `${spot.y * 100}%`,
        width: `${STACK.box * 100}%`,
        transform: 'translateY(-50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        cursor: movable ? 'move' : 'default',
        touchAction: 'none',
        userSelect: 'none',
        outline: movable ? '1px dashed rgba(255,255,255,0.35)' : 'none',
        outlineOffset: '4px',
        borderRadius: '4px',
        textAlign: 'left',
        fontFamily: font ? font.css : "'DejaVu Sans', Verdana, Arial, sans-serif",
        fontWeight: font ? font.weight : 700,
        fontStyle: font && font.italic ? 'italic' : 'normal',
        textTransform: upper ? 'uppercase' : 'none',
        ...(softShadow ? { textShadow: STACK_SHADOW } : lookStyle(look)),
      }}
    >
      {rows.map(([cls, idx], r) => (
        <div
          key={`${b}:${r}`}
          ref={(el) => { rowRefs.current[r] = el; }}
          data-size={cls}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            columnGap: `${STACK.gap}em`,
            width: '100%',
            fontSize: `${cqw * STACK.sizes[cls] * (fit[`${b}:${r}`] || 1)}cqw`,
            lineHeight: STACK.line,
            whiteSpace: 'nowrap',
          }}
        >
          {idx.map((i) => {
            const age = t - times[i];
            const e = age < 0 ? 0 : easeOut(age / STACK.rise);
            return (
              <span
                key={i}
                data-word={i}
                style={{
                  display: 'inline-block',
                  color: cls === 'L' ? palette.active : palette.base,
                  opacity: e,
                  transform: `translateY(${((1 - e) * STACK.drop).toFixed(3)}em)`,
                  ...(look === 'box' ? { background: '#000', padding: '0 0.1em' } : {}),
                }}
              >
                {all[i]}
              </span>
            );
          })}
        </div>
      ))}
    </Box>
  );
};

/** '#RRGGBB' + an alpha → rgba() (the Cherry Box glow). */
const rgba = (hex, a) => {
  const h = String(hex || '#000000').replace('#', '');
  return `rgba(${[0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)).join(',')},${a})`;
};

/**
 * The box family (Podcast Box, Soft Rise, Whisper, Cherry Box, Cherry Pill, Glide Box, Underline
 * Glide, Cherry Glow): a phrase of centred words and a rounded box — or an underline, or (Cherry
 * Glow) the word itself lit up and glowing — in the highlight colour for the word being said, moving the way the preset says (captionPresets boxState, the
 * worker's caption_fx.box_events). The boxes are measured off the laid-out words.
 */
const BoxCaption = ({
  all, times, t, opts, palette, size, vertical, font, position, look, upper,
  onPointerDown, movable, onDoubleClick, testId,
}) => {
  const group = opts.group || 6;
  const blocks = useMemo(() => phraseBlocks(all, Math.min(3, group), group), [all, group]);
  const { k } = wordAt(times, t, 0);
  let b = blocks.findIndex((block) => block.includes(k));
  if (b < 0) b = 0;
  const block = blocks[b] || [];
  const boxRef = useRef(null);
  const wordRefs = useRef({});
  const [laid, setLaid] = useState({ rects: {}, em: 16 });
  // Each word's highlight rectangle, measured where the browser put it (re-measured every frame,
  // stored only when it moved — a font finishing loading, a new phrase).
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const em = parseFloat(window.getComputedStyle(box).fontSize) || 16;
    const rects = {};
    block.forEach((i) => {
      const el = wordRefs.current[i];
      if (el) rects[i] = boxRect(opts, [el.offsetLeft, el.offsetTop, el.offsetWidth, el.offsetHeight], em);
    });
    const moved = Math.abs(em - laid.em) > 0.01 || block.some((i) => !laid.rects[i] || !rects[i]
      || rects[i].some((v, n) => Math.abs(v - laid.rects[i][n]) > 0.25));
    if (moved) setLaid({ rects, em });
  });
  const scale = (font && font.scale) || 1;
  const cqw = (vertical ? size * 0.139 : size * 0.078) * scale;
  const spot = position || { x: 0.5, y: (opts.spot || [0.72, 0.8])[vertical ? 0 : 1] };
  const glowing = opts.shape === 'glow';
  const light = luma(palette.active) > 0.78 && opts.shape !== 'underline' && !glowing;
  const lit = glowing ? new Map(glowState(opts, times, block, t)) : null;
  const current = k >= 0 && block.includes(k) ? k : -1;
  const radius = (opts.radius === undefined ? 0.2 : opts.radius) * laid.em;
  const alpha = opts.alpha === undefined ? 1 : opts.alpha;
  const boxes = glowing ? [] : boxState(opts, laid.rects, times, block, t, laid.em);
  const glowSx = (i) => {
    const a = (lit && lit.get(i)) || 0;
    if (a <= 0.004) return {};
    const glowEm = 2 * (opts.glow === undefined ? 0.12 : opts.glow);
    return {
      color: mixColour(palette.base, palette.active, a),
      textShadow: `0 0 ${glowEm}em ${rgba(palette.active, (opts.alpha === undefined ? 0.85 : opts.alpha) * a)}, ${look === 'shadow' ? STACK_SHADOW : '0 0 0 transparent'}`,
    };
  };
  return (
    <Box
      ref={boxRef}
      data-testid={testId}
      data-preset="box"
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick || undefined}
      title={onDoubleClick ? EXPORT_TEXT.EDIT_WORDS : undefined}
      sx={{
        position: 'absolute',
        left: `${(spot.x - BOX.width / 2) * 100}%`,
        top: `${spot.y * 100}%`,
        width: `${BOX.width * 100}%`,
        transform: 'translateY(-50%)',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        columnGap: `${BOX.gap}em`,
        lineHeight: opts.line || 1.18,
        cursor: movable ? 'move' : 'default',
        touchAction: 'none',
        userSelect: 'none',
        outline: movable ? '1px dashed rgba(255,255,255,0.35)' : 'none',
        outlineOffset: '4px',
        borderRadius: '4px',
        fontFamily: font ? font.css : "'DejaVu Sans', Verdana, Arial, sans-serif",
        fontWeight: font ? font.weight : 700,
        fontStyle: font && font.italic ? 'italic' : 'normal',
        fontSize: `${cqw}cqw`,
        textTransform: upper ? 'uppercase' : 'none',
        ...(look === 'shadow' ? { textShadow: STACK_SHADOW } : lookStyle(look)),
      }}
    >
      {boxes.map(([x, y, w, h, a], n) => (
        <span
          // eslint-disable-next-line react/no-array-index-key
          key={`box${n}`}
          aria-hidden
          data-box={n}
          style={{
            position: 'absolute',
            left: x,
            top: y,
            width: w,
            height: h,
            borderRadius: Math.min(radius, h / 2, w / 2),
            background: palette.active,
            opacity: a * alpha,
            zIndex: 0,
            pointerEvents: 'none',
            ...(opts.glow ? { boxShadow: `0 0 ${0.5 * laid.em}px ${rgba(palette.active, 0.55)}` } : {}),
          }}
        />
      ))}
      {block.map((i) => (
        <span
          key={i}
          ref={(el) => { wordRefs.current[i] = el; }}
          data-word={i}
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'inline-block',
            whiteSpace: 'nowrap',
            color: light && i === current ? '#111111' : palette.base,
            ...(light && i === current ? { textShadow: 'none', WebkitTextStroke: 0 } : {}),
            ...(glowing ? glowSx(i) : {}),
          }}
        >
          {all[i]}
        </span>
      ))}
    </Box>
  );
};

/**
 * The key-word family (Keyword Mix, Keyword Pop, Keyword Box, Elegant Serif, Handwritten, Label,
 * Cherry Script): words appear as they are said; the phrase's key words (its nouns and numbers)
 * are drawn their own way — bigger in another font, on a line of their own, in a pill, circled,
 * glowing; Label puts the phrase on a paper strip. The CSS here (flex, wrap, baseline-aligned
 * lines) is what the worker's caption_fx.kw_layout copies.
 */
const KwCaption = ({
  all, nouns, times, t, opts, palette, size, vertical, font, position, look, upper,
  onPointerDown, movable, onDoubleClick, testId,
}) => {
  const kopt = opts.key || {};
  const strip = opts.strip || null;
  const group = opts.group || 6;
  const blocks = useMemo(() => phraseBlocks(all, Math.min(3, group), group), [all, group]);
  const nounWords = useMemo(() => nounSet(nouns), [nouns]);
  const { k } = wordAt(times, t, 0);
  let b = blocks.findIndex((block) => block.includes(k));
  if (b < 0) b = 0;
  const block = blocks[b] || [];
  const keys = kwKeys(all, block, nounWords, kopt.pick || 'one');
  const boxRef = useRef(null);
  const itemRefs = useRef({});
  const [stripBox, setStripBox] = useState(null);
  // Label's paper strip hugs the words: measured off them (the worker takes the same union).
  useLayoutEffect(() => {
    if (!strip || !boxRef.current) return;
    const els = block.map((i) => itemRefs.current[i]).filter(Boolean);
    if (!els.length) return;
    const em = parseFloat(window.getComputedStyle(boxRef.current).fontSize) || 16;
    const l = Math.min(...els.map((e) => e.offsetLeft));
    const r = Math.max(...els.map((e) => e.offsetLeft + e.offsetWidth));
    const next = { left: l - strip.pad[0] * em, top: -strip.pad[1] * em, width: r - l + 2 * strip.pad[0] * em, height: boxRef.current.offsetHeight + 2 * strip.pad[1] * em, radius: (strip.radius || 0) * em };
    if (!stripBox || ['left', 'top', 'width', 'height'].some((n) => Math.abs(next[n] - stripBox[n]) > 0.25)) setStripBox(next);
  });
  const scale = (font && font.scale) || 1;
  const cqw = (vertical ? size * 0.139 : size * 0.078) * scale;
  const spot = position || { x: 0.5, y: (opts.spot || [0.66, 0.8])[vertical ? 0 : 1] };
  const pick = (c, fallback) => (c === 'active' ? palette.active : (c === 'base' || !c ? fallback : c));
  const wordColour = (strip && strip.ink) || palette.base;
  const keyColour = pick(kopt.colour, wordColour);
  const keyFont = kopt.font ? CAPTION_FONTS.find((f) => f.key === kopt.font) : null;
  const pill = kopt.pill || null;
  const plainLetters = { textShadow: 'none', WebkitTextStroke: 0 };
  const letters = look === 'shadow' ? { textShadow: STACK_SHADOW } : lookStyle(look);
  return (
    <Box
      ref={boxRef}
      data-testid={testId}
      data-preset="kw"
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick || undefined}
      title={onDoubleClick ? EXPORT_TEXT.EDIT_WORDS : undefined}
      sx={{
        position: 'absolute',
        left: `${(spot.x - BOX.width / 2) * 100}%`,
        top: `${spot.y * 100}%`,
        width: `${BOX.width * 100}%`,
        transform: `translateY(-50%)${strip && strip.tilt ? ` rotate(${strip.tilt}deg)` : ''}`,
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'baseline',
        columnGap: `${opts.gap === undefined ? BOX.gap : opts.gap}em`,
        lineHeight: opts.line || 1.12,
        letterSpacing: opts.spacing ? `${opts.spacing}em` : 'normal',
        cursor: movable ? 'move' : 'default',
        touchAction: 'none',
        userSelect: 'none',
        outline: movable ? '1px dashed rgba(255,255,255,0.35)' : 'none',
        outlineOffset: '4px',
        borderRadius: '4px',
        fontFamily: font ? font.css : "'DejaVu Sans', Verdana, Arial, sans-serif",
        fontWeight: font ? font.weight : 700,
        fontStyle: font && font.italic ? 'italic' : 'normal',
        fontSize: `${cqw}cqw`,
        textTransform: opts.case === 'lower' ? 'lowercase' : (upper ? 'uppercase' : 'none'),
        ...(strip ? plainLetters : letters),
      }}
    >
      {strip && stripBox ? (
        <span
          aria-hidden
          data-strip
          style={{ position: 'absolute', left: stripBox.left, top: stripBox.top, width: stripBox.width, height: stripBox.height, borderRadius: stripBox.radius, background: strip.colour, boxShadow: '0 0.2em 0.6em rgba(0,0,0,0.35)', zIndex: 0 }}
        />
      ) : null}
      {block.map((i) => {
        const key = keys.has(i);
        const [a, rise, sc] = kwEnter(opts, key, t - times[i]);
        const onPill = key && pill;
        const glow = key && kopt.glow;
        const colour = onPill ? (pill.text || '#FFFFFF') : (key ? keyColour : wordColour);
        const age = t - times[i];
        const circle = key && kopt.circle ? easeInOut(age / KW.circle) : 0;
        const tilt = opts.tilt ? ` rotate(${opts.tilt}deg)` : '';
        const keyStyle = key ? {
          ...(keyFont ? { fontFamily: keyFont.css, fontWeight: keyFont.weight, fontStyle: keyFont.italic ? 'italic' : 'normal' } : {}),
          ...(kopt.italic ? { fontStyle: 'italic' } : {}),
          ...(kopt.scale ? { fontSize: `${kopt.scale}em` } : {}),
          ...(kopt.line ? { lineHeight: kopt.line } : {}),
          ...(kopt.own_line ? { flexBasis: '100%', textAlign: 'center' } : {}),
        } : {};
        return (
          <span
            key={i}
            ref={(el) => { itemRefs.current[i] = el; }}
            data-word={i}
            data-key={key ? '1' : undefined}
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'inline-block',
              whiteSpace: 'nowrap',
              color: colour,
              ...keyStyle,
              ...(onPill ? {
                padding: `${pill.pad[1]}em ${pill.pad[0]}em`,
                borderRadius: `${pill.radius}em`,
                background: rgba(pick(pill.colour, palette.active), a),
                ...plainLetters,
              } : {}),
              ...(glow ? { textShadow: `0 0 ${2 * glow}em ${rgba(colour, 0.55)}` } : {}),
              opacity: onPill ? 1 : a,
              transform: `translateY(${rise.toFixed(3)}em)${tilt}`,
            }}
          >
            <span style={{ display: 'inline-block', transform: sc !== 1 ? `scale(${sc.toFixed(3)})` : 'none', opacity: onPill ? a : 1 }}>{all[i]}</span>
            {key && kopt.circle && age >= 0 ? (
              <span
                aria-hidden
                data-circle
                style={{
                  position: 'absolute',
                  left: `-${KW.circleInset[0]}em`,
                  right: `-${KW.circleInset[0]}em`,
                  top: `-${KW.circleInset[1]}em`,
                  bottom: `-${KW.circleInset[2]}em`,
                  border: `${KW.circleLine}em solid ${keyColour}`,
                  borderRadius: '50%',
                  transform: 'rotate(-4deg)',
                  clipPath: `inset(0 ${((1 - circle) * 100).toFixed(1)}% 0 0)`,
                  pointerEvents: 'none',
                }}
              />
            ) : null}
          </span>
        );
      })}
    </Box>
  );
};

/**
 * The preview's captions PLAY: each word lights up at its own time in the clip (looping over the
 * poster, or with the clip itself when it plays in the preview), with the same motion the worker
 * burns — pop, wiggle, karaoke, words sliding / fading in, the ball hopping word to word.
 * preset: a caption preset whose layout is its own (Story Stack) draws instead of the knobs.
 * time: a clock from outside (the style picker's tiles share one), else the caption keeps its own.
 */
export const Caption = ({
  text, mode, wordsPerGroup, palette, size, vertical, font, position, onPointerDown, movable, look, upper,
  highlight = 'color', reveal = 'all', bounce = 'none', onDoubleClick = null,
  wordStarts = null, seconds = 0, getTime = null, preset = '', nouns = '', time = null,
  testId = 'export-preview-caption',
}) => {
  const all = useMemo(() => (text || '').split(/\s+/).filter(Boolean), [text]);
  const n = all.length;
  const times = useMemo(() => wordTimes(n, wordStarts, seconds), [n, wordStarts, seconds]);
  const span = Math.max(seconds || 0, (n ? times[n - 1] : 0) + 0.4);
  const ballId = testId === 'export-preview-caption' ? 'export-preview-ball' : `${testId}-ball`;
  const ownClock = useClipClock(n > 0 && time === null, span + FX.hold, getTime);
  const t = time === null ? ownClock : time;
  const { k, age, progress } = wordAt(times, Math.min(t, span), span);
  const [a, b] = shownRange(k, n, mode, wordsPerGroup);
  const scale = (font && font.scale) || 1;
  const bouncing = BOUNCERS.includes(bounce);
  const oneByOne = ONE_BY_ONE.includes(reveal);
  const boxRef = useRef(null);
  const ballRef = useRef(null);
  const wordRefs = useRef([]);

  // The ball / cherry: lands on the word as it starts, squashes, rests, then hops to the next
  // word on screen (the worker's caption_fx hop_path / squash).
  useLayoutEffect(() => {
    if (!bouncing) return;
    const ball = ballRef.current;
    const box = boxRef.current;
    const el = wordRefs.current[k];
    if (!ball || !box || !el) return;
    const fontPx = parseFloat(window.getComputedStyle(box).fontSize) || 16;
    const sizePx = fontPx / scale; // the worker's font size, in preview pixels
    // land just above the capitals and their outline
    const at = (w) => [w.offsetLeft + w.offsetWidth / 2, w.offsetTop + fontPx * 0.12];
    let p = at(el);
    let sc = [1, 1];
    const gap = (k + 1 < n ? times[k + 1] : span) - times[k];
    const hop = Math.min(BOUNCE.hop, 0.8 * gap);
    const next = k + 1 < b ? wordRefs.current[k + 1] : null;
    if (age < BOUNCE.squash) {
      const v = age / BOUNCE.squash;
      sc = [1.4 - 0.4 * v, 0.62 + 0.38 * v];
    } else if (next && age > gap - hop) {
      const u = Math.min(1, (age - (gap - hop)) / hop);
      const q = at(next);
      const h = Math.min(1.5 * sizePx, Math.max(0.55 * sizePx, 0.35 * Math.abs(q[0] - p[0])));
      p = hopAt(p, q, u, h);
      sc = squashAt(u);
    }
    ball.style.opacity = '1';
    ball.style.transform = `translate(${p[0] - (ball.offsetWidth * sc[0]) / 2}px, ${p[1] - ball.offsetHeight * sc[1]}px) scale(${sc[0]}, ${sc[1]})`;
  });

  if (!n) return null;
  if (presetEngine(preset) === 'kw') {
    return (
      <KwCaption
        all={all} nouns={nouns} times={times} t={Math.min(t, span)} opts={presetByKey(preset).opts || {}}
        palette={palette} size={size} vertical={vertical} font={font} position={position} look={look} upper={upper}
        onPointerDown={onPointerDown} movable={movable} onDoubleClick={onDoubleClick} testId={testId}
      />
    );
  }
  if (presetEngine(preset) === 'box') {
    return (
      <BoxCaption
        all={all} times={times} t={Math.min(t, span)} opts={presetByKey(preset).opts || {}} palette={palette}
        size={size} vertical={vertical} font={font} position={position} look={look} upper={upper}
        onPointerDown={onPointerDown} movable={movable} onDoubleClick={onDoubleClick} testId={testId}
      />
    );
  }
  if (presetEngine(preset) === 'stack') {
    return (
      <StackCaption
        all={all} nouns={nouns} times={times} t={Math.min(t, span)} palette={palette} size={size}
        vertical={vertical} font={font} position={position} look={look} upper={upper}
        onPointerDown={onPointerDown} movable={movable} onDoubleClick={onDoubleClick} testId={testId}
      />
    );
  }
  // Font size in container-width units, scaled like the worker's ASS canvas (64 = default),
  // times how big libass really draws this font (CAPTION_FONTS scale — measured).
  const cqw = (vertical ? size * 0.139 : size * 0.078) * scale;
  // A dragged spot is the caption's CENTRE (the worker pins it with \an5\pos); none = the
  // usual place (22% up on vertical, near the bottom on wide).
  const place = position
    ? { left: `${position.x * 100}%`, top: `${position.y * 100}%`, width: '88%', transform: 'translate(-50%, -50%)' }
    : { left: '6%', right: '6%', bottom: vertical ? '22%' : '9%' };
  const wordSx = (j) => {
    const lit = j === k;
    const st = { ...wordStyle(highlight, lit, palette, j < k), ...(oneByOne && j > k ? hiddenStyle : {}) };
    if (!lit) return st;
    if (highlight === 'fill') {
      const pct = Math.round(progress * 100);
      st.backgroundImage = `linear-gradient(90deg, ${palette.active} ${pct}%, ${palette.base} ${pct}%)`;
    }
    return { ...st, ...wordMotion(highlight, reveal, age, oneByOne) };
  };
  return (
    <Box
      ref={boxRef}
      data-testid={testId}
      data-highlight={highlight}
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick || undefined}
      title={onDoubleClick ? EXPORT_TEXT.EDIT_WORDS : undefined}
      sx={{
        position: 'absolute',
        ...place,
        cursor: movable ? 'move' : 'default',
        touchAction: 'none',
        userSelect: 'none',
        outline: movable ? '1px dashed rgba(255,255,255,0.35)' : 'none',
        outlineOffset: '4px',
        borderRadius: '4px',
        textAlign: 'center',
        fontFamily: font ? font.css : "'DejaVu Sans', Verdana, Arial, sans-serif",
        fontWeight: font ? font.weight : 700,
        fontStyle: font && font.italic ? 'italic' : 'normal',
        fontSize: `${cqw}cqw`,
        lineHeight: 1.15,
        textTransform: upper ? 'uppercase' : 'none',
        ...lookStyle(look),
      }}
    >
      <span style={look === 'box' ? BOX_STYLE : undefined}>
        {all.slice(a, b).map((word, i) => {
          const j = a + i;
          return (
            <React.Fragment key={j}>
              {i ? ' ' : ''}
              <span ref={(el) => { wordRefs.current[j] = el; }} style={wordSx(j)}>
                {word}
              </span>
            </React.Fragment>
          );
        })}
      </span>
      {bouncing && bounce === 'cherry' ? (
        <img
          ref={ballRef}
          src={logoSite}
          alt=""
          aria-hidden
          data-testid={ballId}
          style={{ position: 'absolute', left: 0, top: 0, height: `${BOUNCE.cherry / scale}em`, transformOrigin: '0 0', opacity: 0, pointerEvents: 'none', filter: 'drop-shadow(0 0 0.03em #000) drop-shadow(0 0 0.03em #000)' }}
        />
      ) : null}
      {bouncing && bounce === 'cherryball' ? (
        <span
          ref={ballRef}
          aria-hidden
          data-testid={ballId}
          style={{ position: 'absolute', left: 0, top: 0, width: `${BOUNCE.cherryball / scale}em`, height: `${BOUNCE.cherryball / scale}em`, borderRadius: '50%', background: '#FFFFFF', border: `${0.05 / scale}em solid #000`, boxSizing: 'border-box', transformOrigin: '0 0', opacity: 0, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <img src={logoOnLight} alt="" style={{ height: '72%' }} />
        </span>
      ) : null}
      {bouncing && bounce === 'ball' ? (
        <span
          ref={ballRef}
          aria-hidden
          data-testid={ballId}
          style={{ position: 'absolute', left: 0, top: 0, width: `${BOUNCE.ball / scale}em`, height: `${BOUNCE.ball / scale}em`, borderRadius: '50%', background: palette.active, border: `${0.05 / scale}em solid #000`, boxSizing: 'border-box', transformOrigin: '0 0', opacity: 0, pointerEvents: 'none' }}
        />
      ) : null}
    </Box>
  );
};

const panel = {
  borderRadius: '14px',
  background: '#0f0d12',
  border: '1px solid rgba(255,255,255,0.08)',
  boxSizing: 'border-box',
};

const ExportPreview = ({
  kind,
  shape,
  thumbnail,
  text,
  speaker,
  start,
  count,
  captions,
  mode,
  wordsPerGroup,
  palette,
  size,
  font,
  look = 'outline',
  upper = false,
  highlight = 'color',
  reveal = 'all',
  bounce = 'none',
  // A caption preset with its own layout (Story Stack), and the clip's nouns it picks key words from
  preset = '',
  nouns = '',
  // ✏ Edit words (optional): double-click the captions
  onCaptionEdit = null,
  // Reposition (optional): this clip's framing + where the captions sit, and their setters.
  view = null,
  onViewChange = null,
  captionPos = null,
  onCaptionMove = null,
  // Your logo (My Style): {url, x, y, size, opacity} drawn where it will be burned; drag to move.
  logo = null,
  onLogoMove = null,
  // The captions play at the clip's word times (seconds from its start; null = spread evenly);
  // `playing` plays the clip itself (`videoUrl`, from `start` for `seconds`) in the frame.
  wordStarts = null,
  seconds = 0,
  playing = false,
  videoUrl = '',
}) => {
  if (!kind) return null;
  const vertical = shape === 'blur' || shape === 'zoom';

  if (kind === 'mp3') {
    return (
      <Box sx={{ ...panel, width: { xs: '100%', md: 440 }, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', height: 80 }}>
          {Array.from({ length: 48 }, (_, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                borderRadius: '2px',
                background: i < 20 ? '#EC2B57' : 'rgba(255,255,255,0.22)',
                height: `${18 + Math.round(Math.abs(Math.sin(i * 1.7) * 52 + Math.cos(i * 0.6) * 12))}px`,
              }}
            />
          ))}
        </Box>
        <Box sx={{ fontSize: 13, color: '#a79fae' }}>{EXPORT_TEXT.AUDIO_ONLY}</Box>
      </Box>
    );
  }

  if (kind === 'srt' || kind === 'vtt' || kind === 'zip' || kind === 'txt') {
    const line = text || '…';
    const zipWho = speaker ? String(speaker).trim() : 'A';
    const body =
      kind === 'srt'
        ? `1\n00:00:00,000 --> 00:00:04,000\n${line}`
        : kind === 'vtt'
          ? `WEBVTT\n\n00:00:00.000 --> 00:00:04.000\n${line}`
          : kind === 'txt'
            ? buildTranscriptText('', [{ start, speaker, text: line }]) + (count > 1 ? '…' : '')
          // The worker's real names (tasks.py zip_srt): per speaker, all + each emotion.
          : [`${zipWho}_all.srt`, `${zipWho}_Positive.srt`, `${zipWho}_Negative.srt`, `${zipWho}_Neutral.srt`, EXPORT_TEXT.ZIP_FOOT].join('\n');
    return (
      <Box sx={{ ...panel, width: { xs: '100%', md: 450 }, overflow: 'hidden' }}>
        <Box sx={{ px: 1.75, py: 1.25, fontSize: 12, color: '#a79fae', borderBottom: '1px solid rgba(255,255,255,0.07)', fontFamily: 'monospace' }}>
          {kind === 'zip' ? EXPORT_TEXT.ZIP_MORE : `.${kind}`}
        </Box>
        <Box
          data-testid="export-preview-text"
          sx={{ p: 1.75, fontFamily: 'monospace', fontSize: 13, lineHeight: '20px', whiteSpace: 'pre-line', color: '#e8e2ec' }}
        >
          {body}
        </Box>
      </Box>
    );
  }

  if (kind === 'xml') {
    const n = Math.max(1, Math.min(count || 1, 16));
    const widths = Array.from({ length: n }, (_, i) => 2 + ((i * 7) % 4));
    return (
      <Box sx={{ ...panel, width: { xs: '100%', md: 460 }, p: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Box sx={{ fontSize: 13, color: '#a79fae' }}>{EXPORT_TEXT.XML_HINT}</Box>
        <Box sx={{ display: 'flex', gap: '3px', height: 34 }}>
          {widths.map((w, i) => (
            <Box key={i} sx={{ flex: w, borderRadius: '4px', background: i % 2 ? '#c42a55' : '#EC2B57' }} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: '3px', height: 22 }}>
          {widths.map((w, i) => (
            <Box key={i} sx={{ flex: w, borderRadius: '4px', background: i % 2 ? '#2e8f64' : '#39a877' }} />
          ))}
        </Box>
        <Box sx={{ fontSize: 12, color: '#756d7c' }}>{EXPORT_TEXT.xmlFoot(count || 1)}</Box>
      </Box>
    );
  }

  // A picture: video, super cut, audiogram, gif.
  return (
    <PicturePreview
      kind={kind}
      shape={shape}
      vertical={vertical}
      thumbnail={thumbnail}
      captions={captions}
      text={text}
      mode={mode}
      wordsPerGroup={wordsPerGroup}
      palette={palette}
      size={size}
      font={font}
      look={look}
      upper={upper}
      highlight={highlight}
      reveal={reveal}
      bounce={bounce}
      preset={preset}
      nouns={nouns}
      onCaptionEdit={onCaptionEdit}
      view={view}
      onViewChange={onViewChange}
      captionPos={captionPos}
      onCaptionMove={onCaptionMove}
      logo={logo}
      onLogoMove={onLogoMove}
      wordStarts={wordStarts}
      start={start}
      seconds={seconds}
      playing={playing && Boolean(videoUrl)}
      videoUrl={videoUrl}
    />
  );
};

/**
 * The picture preview. Drag the PICTURE to pan the clip (and zoom with the slider below it),
 * drag the CAPTION to move it — the worker crops and pins exactly what this draws
 * (framing.js ↔ reframe.view_crop). Without handlers it is the plain preview it always was.
 */
const PicturePreview = ({
  kind, shape, vertical, thumbnail, captions, text, mode, wordsPerGroup, palette, size, font,
  look, upper, highlight, reveal, bounce, preset, nouns, view, onViewChange, captionPos, onCaptionMove, onCaptionEdit,
  logo, onLogoMove,
  wordStarts, start = 0, seconds = 0, playing = false, videoUrl = '',
}) => {
  const [sourceAspect, setSourceAspect] = useState(16 / 9);
  // ▶ Play clip: the clip itself in the frame, with sound, round and round from its start.
  const videoRef = useRef(null);
  const from = Number(start) || 0;
  const until = from + Math.max(0.3, Number(seconds) || 0);
  useEffect(() => {
    const v = videoRef.current;
    if (!playing || !v || typeof window === 'undefined') return undefined;
    let raf;
    const begin = () => {
      try { v.currentTime = from; } catch (e) { /* not seekable yet */ }
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    };
    // checked every frame (timeupdate comes ~4× a second and would run into the next sentence)
    const loop = () => {
      if (v.currentTime >= until || v.ended) begin();
      raf = window.requestAnimationFrame(loop);
    };
    if (v.readyState >= 1) begin();
    else v.addEventListener('loadedmetadata', begin, { once: true });
    raf = window.requestAnimationFrame(loop);
    return () => {
      window.cancelAnimationFrame(raf);
      v.removeEventListener('loadedmetadata', begin);
      try { v.pause(); } catch (e) { /* gone */ }
    };
  }, [playing, videoUrl, from, until]);
  const clipTime = playing ? () => (videoRef.current ? videoRef.current.currentTime - from : 0) : null;
  // The space the preview is given — the frame is the largest one that fits WHOLE in it, so
  // controls appearing below can shrink it but never cut it off.
  const spaceRef = useRef(null);
  const [space, setSpace] = useState(null);
  useEffect(() => {
    const el = spaceRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setSpace({ w: entry.contentRect.width, h: entry.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const pictureRef = useRef(null);
  const outputRef = useRef(null);
  const drag = useRef(null);

  const isGif = kind === 'gif';
  const isAudiogram = kind === 'audiogram';
  const tall = vertical && !isGif;
  // Super Cut burns captions too (it runs on the same cutter as Video).
  const showCaption = captions && (kind === 'video' || kind === 'super' || isAudiogram);
  const geo = shapeGeometry(isGif ? 'original' : shape, sourceAspect);
  const v = view || DEFAULT_VIEW;
  const win = cropWindow(sourceAspect, geo.picture, v);
  const canPan = Boolean(onViewChange) && !isAudiogram && Boolean(thumbnail);
  const canMoveCaption = Boolean(onCaptionMove) && showCaption;

  const startPan = (e) => {
    if (!canPan || e.button > 0 || !Number.isFinite(e.clientX)) return;
    const r = pictureRef.current.getBoundingClientRect();
    drag.current = { kind: 'pan', x: e.clientX, y: e.clientY, w: r.width, h: r.height, view: v, win };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const startCaption = (e) => {
    if (!canMoveCaption || e.button > 0) return;
    e.stopPropagation();
    drag.current = { kind: 'caption' };
    outputRef.current.setPointerCapture?.(e.pointerId);
  };
  const showLogo = Boolean(logo && logo.url) && !isGif;
  // The logo's own pixels (from its image) and, with a backdrop, the box around it: the same
  // padding the worker draws (brand_logo.with_backdrop), so the preview measures what burns.
  const [logoDims, setLogoDims] = useState(null);
  const backdrop = logo && logo.backdrop && logo.backdrop !== 'none' ? logo.backdrop : null;
  const pad = logoDims && backdrop ? backdropPad(logoDims.w, logoDims.h) : 0;
  const artW = logoDims ? logoDims.w + 2 * pad : 1;
  const artH = logoDims ? logoDims.h + 2 * pad : 1;
  // A quick spot (↖ ↗ ◎ ↙ ↘) follows the shape: recomputed whenever the shape, the picture,
  // the size or the backdrop changes. Dragging clears the spot (free placement).
  const spotX = logo && logo.spot && logoDims ? spotPosition(logo.spot, {
    shape: isGif ? 'original' : shape, sourceAspect, on: logo.on, size: logo.size, logoAspect: artW / artH,
  }) : null;
  useEffect(() => {
    if (!spotX || !onLogoMove) return;
    if (Math.abs(spotX.x - logo.x) > 0.0005 || Math.abs(spotX.y - logo.y) > 0.0005) onLogoMove(spotX, { snap: true });
  }, [spotX && spotX.x, spotX && spotX.y]); // eslint-disable-line react-hooks/exhaustive-deps
  const startLogo = (e) => {
    if (!onLogoMove || e.button > 0) return;
    e.stopPropagation();
    drag.current = { kind: 'logo' };
    outputRef.current.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d || !Number.isFinite(e.clientX) || !Number.isFinite(e.clientY)) return;
    if (d.kind === 'pan') {
      onViewChange(panView(d.view, d.win, (e.clientX - d.x) / d.w, (e.clientY - d.y) / d.h));
    } else if (d.kind === 'logo') {
      const r = outputRef.current.getBoundingClientRect();
      const clamp = (n) => Math.min(Math.max(n, 0), 1);
      onLogoMove({ x: clamp((e.clientX - r.left) / r.width), y: clamp((e.clientY - r.top) / r.height) });
    } else {
      const r = outputRef.current.getBoundingClientRect();
      const clamp = (n) => Math.min(Math.max(n, 0.05), 0.95);
      onCaptionMove({ x: clamp((e.clientX - r.left) / r.width), y: clamp((e.clientY - r.top) / r.height) });
    }
  };
  const endDrag = () => { drag.current = null; };

  // The output frame inside the preview box: the shape's own aspect (Original follows the
  // source), contained. The picture inside it: the whole output for Zoom, the source's
  // aspect (letterboxed) for Wide / Blur / Original.
  const fit = (aspect, within) => (aspect >= within
    ? { width: '100%', height: `${(within / aspect) * 100}%` }
    : { width: `${(aspect / within) * 100}%`, height: '100%' });
  const frameAspect = tall ? 9 / 16 : 16 / 9;
  const outputBox = fit(geo.output, frameAspect);
  const pictureBox = fit(geo.picture, geo.output);
  const centered = { position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
  // Largest frame of the shape's aspect inside the measured space (capped like before:
  // 440 wide / 444 tall). No measurement yet (tests, first paint) → the old fixed sizes.
  let frameSize = null;
  if (space && space.w > 0) {
    const maxW = tall ? 250 : 440;
    const maxH = tall ? 444 : 248;
    const h = space.h > 40 ? Math.min(space.h, maxH) : maxH;
    const w = Math.min(space.w, maxW, h * frameAspect);
    frameSize = { width: Math.floor(w), height: Math.floor(w / frameAspect) };
  }

  return (
    <Box ref={spaceRef} sx={{ width: '100%', height: '100%', minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Box
      data-testid="export-preview-frame"
      data-shape={isGif ? 'gif' : shape}
      sx={{
        position: 'relative',
        flexShrink: 0,
        ...(frameSize
          ? frameSize
          : {
            width: tall ? 200 : { xs: '100%', md: 440 },
            aspectRatio: tall ? '9 / 16' : '16 / 9',
          }),
        maxWidth: '100%',
        borderRadius: tall ? '22px' : '12px',
        overflow: 'hidden',
        background: '#000',
        border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 18px 50px rgba(0,0,0,0.6)',
      }}
    >
      {tall && shape === 'blur' && thumbnail && !isAudiogram ? (
        <img
          src={thumbnail}
          alt=""
          aria-hidden
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(14px)', transform: 'scale(1.2)' }}
        />
      ) : null}
      <Box
        ref={outputRef}
        data-testid="export-preview-output"
        onPointerMove={onMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        sx={{ ...centered, ...outputBox, containerType: 'inline-size', overflow: 'hidden' }}
      >
        {isAudiogram ? (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CherryMark size={tall ? 90 : 80} />
          </Box>
        ) : thumbnail || playing ? (
          <Box
            ref={pictureRef}
            data-testid="export-preview-picture"
            onPointerDown={startPan}
            sx={{ ...centered, ...pictureBox, overflow: 'hidden', cursor: canPan ? 'grab' : 'default', touchAction: 'none' }}
          >
            {playing ? (
              <video
                ref={videoRef}
                data-testid="export-preview-video"
                src={videoUrl}
                poster={thumbnail || undefined}
                playsInline
                preload="auto"
                onLoadedMetadata={(e) => {
                  const { videoWidth: w, videoHeight: h } = e.currentTarget;
                  if (w && h) setSourceAspect(w / h);
                }}
                style={{ ...imageStyle(win), objectFit: 'fill' }}
              />
            ) : (
              <img
                src={thumbnail}
                alt=""
                draggable={false}
                onLoad={(e) => {
                  const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                  if (w && h) setSourceAspect(w / h);
                }}
                style={imageStyle(win)}
              />
            )}
          </Box>
        ) : null}
        {playing && isAudiogram ? (
          // Audio + logo: the clip's sound under the cherry
          <video ref={videoRef} data-testid="export-preview-video" src={videoUrl} playsInline preload="auto" style={{ display: 'none' }} />
        ) : null}
        {isGif ? (
          <Box sx={{ position: 'absolute', top: 10, left: 10, px: 1, py: 0.25, borderRadius: '6px', background: 'rgba(0,0,0,0.65)', fontSize: 12, fontWeight: 700 }}>
            {EXPORT_TEXT.GIF_BADGE}
          </Box>
        ) : null}
        {v.face && canPan ? (
          <Box sx={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', px: 1, py: 0.25, borderRadius: '6px', background: 'rgba(0,0,0,0.7)', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
            {EXPORT_TEXT.FACE_BADGE}
          </Box>
        ) : null}
        {showLogo ? (
          // Centre at (x, y), width = size of the frame — brand_logo.placement on the worker.
          <div
            data-testid="export-preview-logo-box"
            data-backdrop={backdrop || 'none'}
            onPointerDown={startLogo}
            style={{
              position: 'absolute',
              left: `${logo.x * 100}%`,
              top: `${logo.y * 100}%`,
              width: `${logo.size * 100}%`,
              aspectRatio: `${artW} / ${artH}`,
              transform: 'translate(-50%, -50%)',
              opacity: logo.opacity,
              cursor: onLogoMove ? 'grab' : 'default',
              touchAction: 'none',
              zIndex: 2,
              ...(backdrop ? {
                background: backdrop === 'dark' ? 'rgba(12,12,16,0.8)' : 'rgba(255,255,255,0.9)',
                // the worker's radius is 30% of the shorter side, in both directions
                borderRadius: `${((0.3 * Math.min(artW, artH)) / artW) * 100}% / ${((0.3 * Math.min(artW, artH)) / artH) * 100}%`,
              } : {}),
            }}
          >
            <img
              src={logo.url}
              alt=""
              data-testid="export-preview-logo"
              draggable={false}
              onLoad={(e) => {
                const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                if (w && h) setLogoDims({ w, h });
              }}
              style={{
                position: 'absolute',
                left: `${(pad / artW) * 100}%`,
                top: `${(pad / artH) * 100}%`,
                width: `${(logoDims ? logoDims.w / artW : 1) * 100}%`,
                pointerEvents: 'none',
              }}
            />
          </div>
        ) : null}
        {showCaption ? (
          <Caption
            text={text}
            mode={mode}
            wordsPerGroup={wordsPerGroup}
            palette={palette}
            size={size}
            vertical={geo.output < 1}
            font={font}
            look={look}
            upper={upper}
            highlight={highlight}
            reveal={reveal}
            bounce={bounce}
            preset={preset}
            nouns={nouns}
            onDoubleClick={onCaptionEdit}
            position={captionPos}
            movable={canMoveCaption}
            onPointerDown={startCaption}
            wordStarts={wordStarts}
            seconds={seconds}
            getTime={clipTime}
          />
        ) : null}
      </Box>
    </Box>
    </Box>
  );
};

export default ExportPreview;
